// Máy scan bài làm — phần xử lý ảnh chạy TRÊN ĐIỆN THOẠI (không DOM ngoài canvas).
// Server (OpenCV) chỉ tìm 4 góc trên ảnh thu nhỏ; nắn phối cảnh + tăng nét làm ở
// đây trên ảnh gốc, nên ảnh lưu vào máy và ảnh nộp đều là bản độ phân giải cao.
import { integralImage, windowMean } from '../../chalkbridge/activities/recognition/board-mask'

/** 4 góc TL, TR, BR, BL theo toạ độ 0..1 của ảnh nguồn. */
export type Quad = [number, number][]
export type ScanFilter = 'original' | 'enhance' | 'bw'

const MAX_SOURCE_SIDE = 3200   // ảnh camera 12MP → thu về ~3200px để không hết RAM điện thoại
const MAX_OUTPUT_SIDE = 2400   // trang A4 dọc ra ~1700×2400: đủ nét cho chữ viết tay

export const FULL_QUAD: Quad = [[0, 0], [1, 0], [1, 1], [0, 1]]
export const DEFAULT_QUAD: Quad = [[0.06, 0.06], [0.94, 0.06], [0.94, 0.94], [0.06, 0.94]]

function makeCanvas(w: number, h: number): HTMLCanvasElement {
  const c = document.createElement('canvas')
  c.width = w; c.height = h
  return c
}

/** Đọc ảnh chụp thành canvas (thẻ <img> tự xoay đúng chiều theo EXIF). */
export function loadSource(file: File): Promise<HTMLCanvasElement> {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file)
    const img = new Image()
    img.onload = () => {
      const scale = Math.min(1, MAX_SOURCE_SIDE / Math.max(img.naturalWidth, img.naturalHeight))
      const c = makeCanvas(Math.round(img.naturalWidth * scale), Math.round(img.naturalHeight * scale))
      c.getContext('2d')!.drawImage(img, 0, 0, c.width, c.height)
      URL.revokeObjectURL(url)
      resolve(c)
    }
    img.onerror = () => { URL.revokeObjectURL(url); reject(new Error('Không đọc được ảnh')) }
    img.src = url
  })
}

export function canvasToBlob(canvas: HTMLCanvasElement, quality = 0.9, maxSide = 0): Promise<Blob> {
  let src = canvas
  if (maxSide && Math.max(canvas.width, canvas.height) > maxSide) {
    const s = maxSide / Math.max(canvas.width, canvas.height)
    src = makeCanvas(Math.round(canvas.width * s), Math.round(canvas.height * s))
    src.getContext('2d')!.drawImage(canvas, 0, 0, src.width, src.height)
  }
  return new Promise((resolve, reject) =>
    src.toBlob((b) => (b ? resolve(b) : reject(new Error('Không xuất được ảnh'))), 'image/jpeg', quality))
}

// ── Nắn phối cảnh ─────────────────────────────────────────────────────────────

/** Ma trận 3×3 (h33 = 1) đưa điểm `from[i]` → `to[i]` — khử Gauss hệ 8×8. */
function solveHomography(from: [number, number][], to: [number, number][]): number[] {
  const A: number[][] = []
  for (let i = 0; i < 4; i++) {
    const [x, y] = from[i]; const [X, Y] = to[i]
    A.push([x, y, 1, 0, 0, 0, -x * X, -y * X, X])
    A.push([0, 0, 0, x, y, 1, -x * Y, -y * Y, Y])
  }
  for (let col = 0; col < 8; col++) {
    let pivot = col
    for (let r = col + 1; r < 8; r++) if (Math.abs(A[r][col]) > Math.abs(A[pivot][col])) pivot = r
    ;[A[col], A[pivot]] = [A[pivot], A[col]]
    for (let r = 0; r < 8; r++) {
      if (r === col) continue
      const f = A[r][col] / A[col][col]
      for (let k = col; k < 9; k++) A[r][k] -= f * A[col][k]
    }
  }
  return [...A.map((row, i) => row[8] / row[i]), 1]
}

const dist = (a: [number, number], b: [number, number]) => Math.hypot(a[0] - b[0], a[1] - b[1])

/** Cắt tứ giác `quad` khỏi ảnh nguồn và kéo thẳng thành hình chữ nhật. */
export function warpQuad(source: HTMLCanvasElement, quad: Quad): HTMLCanvasElement {
  const sw = source.width, sh = source.height
  const q = quad.map(([x, y]) => [x * sw, y * sh] as [number, number])
  let w = Math.max(dist(q[0], q[1]), dist(q[3], q[2]))
  let h = Math.max(dist(q[0], q[3]), dist(q[1], q[2]))
  const s = Math.min(1, MAX_OUTPUT_SIDE / Math.max(w, h))
  w = Math.max(1, Math.round(w * s)); h = Math.max(1, Math.round(h * s))

  // Ánh xạ NGƯỢC: điểm ảnh đích (u,v) → vị trí trong ảnh nguồn, rồi nội suy song tuyến.
  const H = solveHomography([[0, 0], [w, 0], [w, h], [0, h]], q)
  const src = source.getContext('2d')!.getImageData(0, 0, sw, sh).data
  const out = makeCanvas(w, h)
  const octx = out.getContext('2d')!
  const img = octx.createImageData(w, h)
  const dst = img.data
  for (let v = 0; v < h; v++) {
    for (let u = 0; u < w; u++) {
      const d = H[6] * u + H[7] * v + 1
      const x = Math.min(sw - 1.001, Math.max(0, (H[0] * u + H[1] * v + H[2]) / d))
      const y = Math.min(sh - 1.001, Math.max(0, (H[3] * u + H[4] * v + H[5]) / d))
      const x0 = x | 0, y0 = y | 0, fx = x - x0, fy = y - y0
      const i00 = (y0 * sw + x0) * 4, i10 = i00 + 4, i01 = i00 + sw * 4, i11 = i01 + 4
      const o = (v * w + u) * 4
      for (let c = 0; c < 3; c++) {
        const top = src[i00 + c] + (src[i10 + c] - src[i00 + c]) * fx
        const bot = src[i01 + c] + (src[i11 + c] - src[i01 + c]) * fx
        dst[o + c] = top + (bot - top) * fy
      }
      dst[o + 3] = 255
    }
  }
  octx.putImageData(img, 0, 0)
  return out
}

export function rotate90(canvas: HTMLCanvasElement, turns: number): HTMLCanvasElement {
  const t = ((turns % 4) + 4) % 4
  if (t === 0) return canvas
  const out = t % 2 ? makeCanvas(canvas.height, canvas.width) : makeCanvas(canvas.width, canvas.height)
  const ctx = out.getContext('2d')!
  ctx.translate(out.width / 2, out.height / 2)
  ctx.rotate((t * Math.PI) / 2)
  ctx.drawImage(canvas, -canvas.width / 2, -canvas.height / 2)
  return out
}

// ── Bộ lọc ────────────────────────────────────────────────────────────────────

function luminance(d: Uint8ClampedArray, n: number): Float32Array {
  const L = new Float32Array(n)
  for (let i = 0; i < n; i++) L[i] = 0.299 * d[i * 4] + 0.587 * d[i * 4 + 1] + 0.114 * d[i * 4 + 2]
  return L
}

/**
 * - `enhance`: chia cho độ sáng nền cục bộ (khử bóng tay/đèn, giấy về trắng, mực
 *   giữ màu) rồi làm nét (unsharp mask).
 * - `bw`: ngưỡng thích nghi kiểu Bradley — mực tối hơn nền xung quanh ~12% là đen;
 *   dải chuyển mềm để nét chữ không răng cưa.
 * Nền cục bộ dùng integral image của ChalkBridge (board-mask), O(1) mỗi điểm ảnh.
 */
export function applyFilter(canvas: HTMLCanvasElement, filter: ScanFilter): HTMLCanvasElement {
  if (filter === 'original') return canvas
  const w = canvas.width, h = canvas.height, n = w * h
  const out = makeCanvas(w, h)
  const ctx = out.getContext('2d')!
  const img = canvas.getContext('2d')!.getImageData(0, 0, w, h)
  const d = img.data
  const L = luminance(d, n)
  const side = Math.min(w, h)

  if (filter === 'bw') {
    const r = Math.max(8, Math.round(side / 60))
    const integral = integralImage(L, w, h)
    for (let y = 0; y < h; y++) {
      for (let x = 0; x < w; x++) {
        const i = y * w + x
        const ratio = L[i] / Math.max(1, windowMean(integral, w, h, x, y, r))
        const v = Math.max(0, Math.min(255, ((ratio - 0.8) / 0.1) * 255))
        d[i * 4] = d[i * 4 + 1] = d[i * 4 + 2] = v
      }
    }
    ctx.putImageData(img, 0, 0)
    return out
  }

  // enhance — bước 1: khử bóng. Cửa sổ lớn (≈1/16 cạnh) để nền không bị nét chữ kéo tối.
  // t = sáng / nền: giấy ≈1 → trắng; chỉ nhân hệ số thì mực NHẠT theo, nên qua đường
  // cong t^1.8 (bảng tra 1024 mức) để mực đậm lại, mực màu vẫn giữ màu.
  const LUT_N = 1024
  const curve = new Uint8ClampedArray(LUT_N + 1)
  for (let k = 0; k <= LUT_N; k++) curve[k] = 255 * Math.pow(k / LUT_N, 1.8)
  const bgR = Math.max(16, Math.round(side / 16))
  let integral = integralImage(L, w, h)
  for (let y = 0; y < h; y++) {
    for (let x = 0; x < w; x++) {
      const i = y * w + x
      const norm = LUT_N / Math.max(40, windowMean(integral, w, h, x, y, bgR) * 0.92)
      for (let c = 0; c < 3; c++) d[i * 4 + c] = curve[Math.min(LUT_N, (d[i * 4 + c] * norm) | 0)]
    }
  }
  // bước 2: làm nét — cộng thêm phần chênh giữa độ sáng và bản làm mờ của nó.
  const L2 = luminance(d, n)
  integral = integralImage(L2, w, h)
  const sharpR = Math.max(1, Math.round(side / 900))
  for (let y = 0; y < h; y++) {
    for (let x = 0; x < w; x++) {
      const i = y * w + x
      const delta = 0.8 * (L2[i] - windowMean(integral, w, h, x, y, sharpR))
      for (let c = 0; c < 3; c++) d[i * 4 + c] = d[i * 4 + c] + delta
    }
  }
  ctx.putImageData(img, 0, 0)
  return out
}

// ── Lưu vào máy ───────────────────────────────────────────────────────────────

/**
 * Điện thoại: mở bảng Chia sẻ (iPhone chọn "Lưu hình ảnh" → vào Ảnh; Android chọn
 * app ảnh/Drive). Máy tính hoặc trình duyệt không hỗ trợ → tải file xuống.
 * Phải gọi TRỰC TIẾP trong sự kiện bấm nút (Safari chặn share sau khi đã await lâu).
 */
export async function saveToDevice(files: File[]): Promise<'shared' | 'downloaded' | 'cancelled'> {
  const nav = navigator as Navigator & { canShare?: (d: ShareData) => boolean }
  const touch = (navigator.maxTouchPoints || 0) > 0
  if (touch && nav.canShare?.({ files }) && navigator.share) {
    try {
      await navigator.share({ files, title: 'Bài làm đã scan' })
      return 'shared'
    } catch (e) {
      if ((e as Error)?.name === 'AbortError') return 'cancelled'
      // lỗi khác (không cho chia sẻ) → rơi xuống tải về
    }
  }
  for (let i = 0; i < files.length; i++) {
    const url = URL.createObjectURL(files[i])
    const a = document.createElement('a')
    a.href = url; a.download = files[i].name
    document.body.appendChild(a); a.click(); a.remove()
    setTimeout(() => URL.revokeObjectURL(url), 4000)
    if (i < files.length - 1) await new Promise((r) => setTimeout(r, 350))
  }
  return 'downloaded'
}
